import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Container,
  Group,
  Pagination,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Search } from 'lucide-react';
import PageHeader from '@components/shared/PageHeader';
import {
  legacyUsersListQueryKey,
  useLegacyUsersListQuery,
} from '../hooks/queries/useLegacyUsersListQuery';
import {
  downloadMigratedLegacyUsersExcel,
  generateAllLegacyV1MigrationExcelData,
  generateAllMigrationJsonData,
  migrateLegacyUser,
  resetMigratedLegacyUsers,
  updateLegacyUserMigrationStatus,
  type LegacyUserListItem,
} from '../services/legacyUserManager';
import { TableLoadingSkeleton } from './components/TableLoadingSkeleton';

const PAGE_SIZE = 25;
const COL_COUNT = 24;

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function CountCell({ value }: { value: number | undefined }) {
  const n = value ?? 0;
  if (n <= 0) {
    return (
      <Text size='sm' c='dimmed' ta='right'>
        —
      </Text>
    );
  }
  return (
    <Text size='sm' fw={600} ta='right'>
      {n}
    </Text>
  );
}

function migrationBadge(user: LegacyUserListItem) {
  const status = user.migration?.status ?? 'pending';
  if (status === 'ignored') {
    return (
      <Badge color='gray' variant='filled'>
        ignored
      </Badge>
    );
  }
  if (status === 'migrated') {
    return (
      <Badge color='teal' variant='light'>
        migrated
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge color='red' variant='light'>
        failed
      </Badge>
    );
  }
  return (
    <Badge color='blue' variant='light'>
      to migrate
    </Badge>
  );
}

export default function AdminLegacyUsers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [migrationStatus, setMigrationStatus] = useState<
    'all' | 'pending' | 'ignored' | 'migrated'
  >('pending');
  const [configurationsFilter, setConfigurationsFilter] = useState<
    'all' | 'with' | 'without'
  >('without');
  const [savingUserId, setSavingUserId] = useState<number | null>(null);
  const [migratingUserId, setMigratingUserId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const [generatingJson, setGeneratingJson] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);
  const [resettingMigrated, setResettingMigrated] = useState(false);
  const [jsonProgress, setJsonProgress] = useState<string | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useLegacyUsersListQuery(
    page,
    PAGE_SIZE,
    debouncedSearch,
    migrationStatus,
    configurationsFilter
  );

  const users = data?.users ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;
  const totalRecords = data?.pagination.totalRecords ?? 0;

  const invalidateList = async () => {
    await queryClient.invalidateQueries({
      queryKey: legacyUsersListQueryKey(
        page,
        PAGE_SIZE,
        debouncedSearch,
        migrationStatus,
        configurationsFilter
      ),
    });
    await refetch();
  };

  const handleIgnoreToggle = async (
    event: MouseEvent,
    user: LegacyUserListItem
  ) => {
    event.stopPropagation();
    const current = user.migration?.status ?? 'pending';
    if (current === 'migrated') {
      if (
        !window.confirm(
          `Re-open user #${user.id} for migrate? Status → pending (be2025 data kept).`
        )
      ) {
        return;
      }
    }
    const nextStatus =
      current === 'ignored' || current === 'migrated' ? 'pending' : 'ignored';
    setSavingUserId(user.id);
    try {
      await updateLegacyUserMigrationStatus(user.id, nextStatus);
      notifications.show({
        color: 'green',
        message:
          nextStatus === 'ignored'
            ? `User #${user.id} marked as ignored`
            : current === 'migrated'
              ? `User #${user.id} re-opened for migrate`
              : `User #${user.id} back to migrate`,
      });
      await invalidateList();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Failed to update status',
      });
    } finally {
      setSavingUserId(null);
    }
  };

  const handleMigrate = async (event: MouseEvent, user: LegacyUserListItem) => {
    event.stopPropagation();
    const status = user.migration?.status ?? 'pending';
    if (status === 'ignored' || status === 'migrated') return;
    if (
      !window.confirm(
        `Migrate courses/videos for user #${user.id} (${user.email}) into be2025?`
      )
    ) {
      return;
    }
    setMigratingUserId(user.id);
    try {
      const result = await migrateLegacyUser(user.id, { dryRun: false });
      if (!result.dryRun) {
        notifications.show({
          color: 'green',
          message: `Migrated #${user.id} → user ${result.applied.new_user_id} (${result.applied.courses_upserted} courses, ${result.applied.products_upserted} products)`,
        });
      }
      await invalidateList();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Migration failed',
      });
      await invalidateList();
    } finally {
      setMigratingUserId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadMigratedLegacyUsersExcel();
      notifications.show({
        color: 'green',
        message: 'Export downloaded',
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Export failed',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleResetMigrated = async () => {
    if (
      !window.confirm(
        'Reset all migrated v1 contacts to pending?\nbe2025 data is kept — only the migration status returns to the starting state.'
      )
    ) {
      return;
    }
    setResettingMigrated(true);
    try {
      const result = await resetMigratedLegacyUsers();
      notifications.show({
        color: 'green',
        message:
          result.reset === 0
            ? 'No migrated contacts to reset'
            : `Reset ${result.reset} migrated contact(s) → pending`,
      });
      await invalidateList();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Reset migrated failed',
      });
    } finally {
      setResettingMigrated(false);
    }
  };

  const handleGenerateAllJson = async () => {
    const migrationLabel =
      migrationStatus === 'pending'
        ? 'To migrate'
        : migrationStatus === 'ignored'
          ? 'Ignored'
          : migrationStatus === 'migrated'
            ? 'Re-open'
            : 'All';
    const configsLabel =
      configurationsFilter === 'with'
        ? 'With configs'
        : configurationsFilter === 'without'
          ? 'No configs'
          : 'Configs: all';
    const filterSummary = `${migrationLabel} · ${configsLabel}`;

    if (
      !window.confirm(
        `Generate migration JSON for filtered users (${filterSummary}) into migration/v1/? This may take a while.`
      )
    ) {
      return;
    }
    setGeneratingJson(true);
    setJsonProgress(`Listing users (${filterSummary})…`);
    try {
      const result = await generateAllMigrationJsonData({
        migrationStatus,
        configurations: configurationsFilter,
        onProgress: ({ done, total, userId, ok, path, error }) => {
          setJsonProgress(
            ok
              ? `${done}/${total} · #${userId} → ${path}`
              : `${done}/${total} · #${userId} FAILED: ${error}`
          );
        },
      });
      notifications.show({
        color: result.failed.length === 0 ? 'green' : 'orange',
        message:
          result.failed.length === 0
            ? `Generated ${result.saved}/${result.total} JSON files (${filterSummary})`
            : `Saved ${result.saved}/${result.total}; ${result.failed.length} failed (see console)`,
      });
      if (result.failed.length > 0) {
        console.warn('generateAllMigrationJsonData failures', result.failed);
      }
      setJsonProgress(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Generate all JSON failed',
      });
      setJsonProgress(null);
    } finally {
      setGeneratingJson(false);
    }
  };

  const handleGenerateAllExcel = async () => {
    const migrationLabel =
      migrationStatus === 'pending'
        ? 'To migrate'
        : migrationStatus === 'ignored'
          ? 'Ignored'
          : migrationStatus === 'migrated'
            ? 'Re-open'
            : 'All';
    const configsLabel =
      configurationsFilter === 'with'
        ? 'With configs'
        : configurationsFilter === 'without'
          ? 'No configs'
          : 'Configs: all';
    const filterSummary = `${migrationLabel} · ${configsLabel}`;

    if (
      !window.confirm(
        `Download Excel (Utenti + Configs) for filtered v1 users (${filterSummary})?`
      )
    ) {
      return;
    }
    setGeneratingExcel(true);
    setJsonProgress(`Listing users for Excel (${filterSummary})…`);
    try {
      const result = await generateAllLegacyV1MigrationExcelData({
        migrationStatus,
        configurations: configurationsFilter,
        onProgress: ({ done, total, userId, ok, filename, error }) => {
          setJsonProgress(
            filename
              ? `Writing ${filename}…`
              : ok
                ? `${done}/${total} · #${userId}`
                : `${done}/${total} · #${userId} FAILED: ${error}`
          );
        },
      });
      notifications.show({
        color:
          result.downloaded > 0 && result.failed.length === 0
            ? 'green'
            : result.downloaded > 0
              ? 'orange'
              : 'red',
        message:
          result.downloaded > 0
            ? result.failed.length === 0
              ? `Downloaded ${result.filename} (${result.total} contacts)`
              : `Downloaded ${result.filename}; ${result.failed.length} failed (see console)`
            : `No Excel generated (${result.failed.length} failed)`,
      });
      if (result.failed.length > 0) {
        console.warn('generateAllLegacyV1MigrationExcelData failures', result.failed);
      }
      setJsonProgress(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Generate all Excel failed',
      });
      setJsonProgress(null);
    } finally {
      setGeneratingExcel(false);
    }
  };

  const rows = useMemo(
    () =>
      users.map((user: LegacyUserListItem) => {
        const isIgnored = user.migration?.status === 'ignored';
        return (
          <Table.Tr
            key={user.id}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/admin/legacy-users/${user.id}`)}
          >
            <Table.Td>{user.id}</Table.Td>
            <Table.Td>
              <Stack gap={2}>
                <Text size='sm' fw={600} style={{ whiteSpace: 'nowrap' }}>
                  {user.display_name || user.name || user.email}
                </Text>
                {user.bes_type ? (
                  <Badge size='xs' variant='outline'>
                    {user.bes_type}
                  </Badge>
                ) : null}
              </Stack>
            </Table.Td>
            <Table.Td>
              <Text size='sm' style={{ whiteSpace: 'nowrap' }}>
                {user.email}
              </Text>
            </Table.Td>
            <Table.Td>
              <Group gap={4} wrap='nowrap'>
                {user.deleted_at ? (
                  <Badge color='red' variant='light'>
                    deleted
                  </Badge>
                ) : (
                  <Badge color='green' variant='light'>
                    active
                  </Badge>
                )}
                {migrationBadge(user)}
              </Group>
            </Table.Td>
            <Table.Td>
              <Group gap={4}>
                {user.has_user_info && (
                  <Badge size='xs' variant='outline'>
                    info
                  </Badge>
                )}
                {user.has_bes_users_profile && (
                  <Badge size='xs' variant='outline' color='violet'>
                    Users
                  </Badge>
                )}
                {user.has_customer_data && (
                  <Badge size='xs' variant='outline'>
                    billing
                  </Badge>
                )}
                {(user.subscriptions_count ?? 0) > 0 && (
                  <Badge size='xs' variant='outline' color='teal'>
                    subs {user.subscriptions_count}
                  </Badge>
                )}
                {(user.payment_intents_count ?? 0) > 0 ||
                user.has_payment_intents ? (
                  <Badge size='xs' variant='outline' color='orange'>
                    pi {user.payment_intents_count ?? '✓'}
                  </Badge>
                ) : null}
                {user.has_password && (
                  <Badge size='xs' variant='outline'>
                    pwd
                  </Badge>
                )}
              </Group>
            </Table.Td>
            <Table.Td>
              <CountCell value={user.courses_linked_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.courses_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.course_progress_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.course_progress_old_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.answers_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.feedback_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.course_tests_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.videos_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.patients_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.devices_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.configurations_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.food_lists_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.food_list_items_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.food_results_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.professionals_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.subscriptions_count} />
            </Table.Td>
            <Table.Td>
              <CountCell value={user.payment_intents_count} />
            </Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>
              {formatDate(user.created_at)}
            </Table.Td>
            <Table.Td>
              <Group gap={4} wrap='nowrap' onClick={(e) => e.stopPropagation()}>
                <Button
                  size='xs'
                  color='teal'
                  variant='light'
                  disabled={
                    isIgnored || user.migration?.status === 'migrated'
                  }
                  loading={migratingUserId === user.id}
                  onClick={(event) => handleMigrate(event, user)}
                >
                  Migrate
                </Button>
                <Button
                  size='xs'
                  color='cyan'
                  variant='light'
                  disabled={
                    isIgnored || user.migration?.status === 'migrated'
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(
                      `/admin/legacy-users/${user.id}/preview`,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >
                  Preview
                </Button>
                <Button
                  size='xs'
                  color={
                    user.migration?.status === 'migrated'
                      ? 'orange'
                      : isIgnored
                        ? 'blue'
                        : 'gray'
                  }
                  variant={
                    isIgnored || user.migration?.status === 'migrated'
                      ? 'light'
                      : 'filled'
                  }
                  loading={savingUserId === user.id}
                  onClick={(event) => handleIgnoreToggle(event, user)}
                >
                  {user.migration?.status === 'migrated'
                    ? 'Re-open'
                    : isIgnored
                      ? 'Undo ignore'
                      : 'To ignore'}
                </Button>
              </Group>
            </Table.Td>
          </Table.Tr>
        );
      }),
    [
      users,
      navigate,
      savingUserId,
      migratingUserId,
    ]
  );

  return (
    <Container size='100%' py='lg' px='md'>
      <Stack gap='lg'>
        <PageHeader
          title='Legacy Users (vapor)'
          subtitle='Reads staging RDS vapor (not production). Related rows are often sparse vs the classic admin / prod dump (~1111 users).'
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration', href: '/admin/legacy-migration' },
            { label: 'Legacy users' },
          ]}
          showBackButton
          backUrl='/admin/legacy-migration'
          actions={
            <Group>
              <Button
                color='orange'
                variant='light'
                loading={resettingMigrated}
                disabled={generatingJson || generatingExcel || exporting}
                onClick={() => void handleResetMigrated()}
              >
                Reset migrated
              </Button>
              <Button
                color='blue'
                variant='light'
                loading={generatingJson}
                disabled={generatingExcel || resettingMigrated}
                onClick={handleGenerateAllJson}
              >
                Generate all JSON data
              </Button>
              <Button
                color='teal'
                variant='light'
                loading={generatingExcel}
                disabled={generatingJson || resettingMigrated}
                onClick={handleGenerateAllExcel}
              >
                Generate all Excel
              </Button>
              <Button
                variant='light'
                loading={exporting}
                disabled={resettingMigrated}
                onClick={handleExport}
              >
                Export Excel
              </Button>
              <Badge color='yellow' variant='filled'>
                vapor read-only
              </Badge>
            </Group>
          }
        />

        {jsonProgress ? (
          <Text size='sm' c='dimmed'>
            {jsonProgress}
          </Text>
        ) : null}
        <Paper withBorder p='md' radius='md'>
          <Group justify='space-between' mb='md' wrap='wrap'>
            <TextInput
              placeholder='Search email, name, or id'
              leftSection={<Search size={16} />}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
                setPage(1);
              }}
              style={{ flex: 1, minWidth: 240 }}
            />
            <SegmentedControl
              value={migrationStatus}
              onChange={(value) => {
                setMigrationStatus(
                  value as 'all' | 'pending' | 'ignored' | 'migrated'
                );
                setPage(1);
              }}
              data={[
                { label: 'All', value: 'all' },
                { label: 'To migrate', value: 'pending' },
                { label: 'Ignored', value: 'ignored' },
                { label: 'Re-open', value: 'migrated' },
              ]}
            />
            <SegmentedControl
              value={configurationsFilter}
              onChange={(value) => {
                setConfigurationsFilter(
                  value as 'all' | 'with' | 'without'
                );
                setPage(1);
              }}
              data={[
                { label: 'Configs: all', value: 'all' },
                { label: 'With configs', value: 'with' },
                { label: 'No configs', value: 'without' },
              ]}
            />
            <Group>
              <Text size='sm' c='dimmed'>
                {totalRecords} users
                {isFetching ? ' · refreshing…' : ''}
              </Text>
              <Button variant='light' onClick={() => refetch()}>
                Refresh
              </Button>
            </Group>
          </Group>
          {configurationsFilter === 'with' && migrationStatus === 'pending' ? (
            <Text size='xs' c='orange'>
              Filtro attuale: With configs ∩ To migrate. Su staging spesso sono
              pochi (es. 2). Metti Migration = All per vedere tutti gli user con
              configurations su questo DB (dump production ~34).
            </Text>
          ) : null}

          {error ? (
            <Text c='red'>{(error as Error).message}</Text>
          ) : isLoading ? (
            <TableLoadingSkeleton rows={8} columns={COL_COUNT} />
          ) : (
            <>
              <ScrollArea type='auto' offsetScrollbars>
                <Table
                  striped
                  highlightOnHover
                  withTableBorder
                  stickyHeader
                  style={{ minWidth: 1400 }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Flags</Table.Th>
                      <Table.Th ta='right'>Linked courses</Table.Th>
                      <Table.Th ta='right'>Entitlements</Table.Th>
                      <Table.Th ta='right'>Progress</Table.Th>
                      <Table.Th ta='right'>Progress old</Table.Th>
                      <Table.Th ta='right'>Answers</Table.Th>
                      <Table.Th ta='right'>Feedback</Table.Th>
                      <Table.Th ta='right'>Tests</Table.Th>
                      <Table.Th ta='right'>Videos</Table.Th>
                      <Table.Th ta='right'>Patients</Table.Th>
                      <Table.Th ta='right'>Devices</Table.Th>
                      <Table.Th ta='right'>Configs</Table.Th>
                      <Table.Th ta='right'>Food lists</Table.Th>
                      <Table.Th ta='right'>Food items</Table.Th>
                      <Table.Th ta='right'>Food results</Table.Th>
                      <Table.Th ta='right'>Professionals</Table.Th>
                      <Table.Th ta='right'>Subs</Table.Th>
                      <Table.Th ta='right'>Pay intents</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.length > 0 ? (
                      rows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={COL_COUNT}>
                          <Text c='dimmed' ta='center' py='md'>
                            No legacy users found
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              <Group justify='center' mt='md'>
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                />
              </Group>
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
